import { format } from 'date-fns';

const normType = (s) => String(s || '').trim().toUpperCase();
export const addNullable = (acc, v) => (v === null || v === undefined ? acc : (acc ?? 0) + Number(v));

// ---------------------------------------------------------------------------
// Metric columns (% Rev / $POR / $PAR) + USALI views (Dept / GOP)
//
// % Rev denominator precedence (per row):
//   1. revenue-account links (data.revenueBasisLinks) — the account's own
//      links, else its department's. A link ties an expense source to one or
//      more REVENUE accounts (e.g. "Server Cost" ÷ Restaurant Food revenue
//      only). Targets are resolved at the row's own location by the linked
//      account's accountNumber, so one org-level mapping covers every
//      property sharing the chart.
//   2. stripped department-name match — role words (Rev/Revenue/Exp/Expense/
//      Cost/COGS/Income…) removed from both sides, so "Food Exp" pairs with
//      "Food Rev". Exact matches are a subset of this.
//   3. location total revenue.
// $POR / $PAR divide by occupied / available rooms from the stat-account
// mappings (data.roomStats.byLocationMonth).
//
// buildFinancialUsaliMatrix regroups the same leaves into the USALI summary
// operating statement: departments pair into operating units via the same
// links/stem matching, each unit lands in a section (Operated /
// Undistributed / Non-operating — explicit chartOfAccountDepartments.
// usaliSection, else inferred), and the statement reads Departmental Profit →
// Total Operated Department Profit → Undistributed → GOP → Non-operating →
// Net Income. Every leaf lands in exactly one unit, so Net Income ties to
// the income-statement location total.
// ---------------------------------------------------------------------------

const REVENUE_TYPE = 'REVENUE';
const INCOME_TYPES = new Set([REVENUE_TYPE, 'OTHER INCOME']);
// Account types that read as below-the-GOP-line when a department carries no
// explicit USALI section.
const NON_OPERATING_TYPES = new Set(['OTHER INCOME', 'OTHER EXPENSE']);
// A department NAMED non-operating/fixed-charges is below the GOP line even
// when its accounts carry generic Expense types and no usaliSection override
// exists (org 13 has no usaliSection assignments and $325K of fixed charges
// deflated the GOP view). Mirrors azfApi/utils/usaliGop.js.
export const NON_OPERATING_NAME = /\bnon[\s-]?operating\b|\bfixed\s+(?:charges?|costs?|expenses?)\b/i;
// The operated unit(s) whose revenue prices ADR / RevPAR on the summary
// statement's Statistics block.
const ROOMS_UNIT_NAME = /\brooms?\b/i;

export const USALI_SECTIONS = {
  OPERATED: 'OPERATED',
  UNDISTRIBUTED: 'UNDISTRIBUTED',
  // Below-GOP bands, in statement order. NON_OPERATING is the classic
  // "fixed charges" band (rent, property taxes, insurance) that closes on
  // EBITDA; MANAGEMENT_FEE sits between GOP and the fixed charges (Income
  // Before Fixed Charges); BELOW_EBITDA holds interest, depreciation &
  // amortization, income taxes and other items between EBITDA and Net Income.
  MANAGEMENT_FEE: 'MANAGEMENT_FEE',
  NON_OPERATING: 'NON_OPERATING',
  BELOW_EBITDA: 'BELOW_EBITDA',
};
// Conflicting overrides inside one paired unit resolve top-down.
export const USALI_PRECEDENCE = [
  USALI_SECTIONS.OPERATED,
  USALI_SECTIONS.UNDISTRIBUTED,
  USALI_SECTIONS.MANAGEMENT_FEE,
  USALI_SECTIONS.NON_OPERATING,
  USALI_SECTIONS.BELOW_EBITDA,
];
// Every band strictly below GOP — these close on Net Income.
export const BELOW_GOP_SECTIONS = [USALI_SECTIONS.MANAGEMENT_FEE, USALI_SECTIONS.NON_OPERATING, USALI_SECTIONS.BELOW_EBITDA];

// Role words stripped before department names are compared. Validated against
// LODGEIC_DEV charts (2026-07): stripping these pairs the suffix-style charts
// with zero ambiguous matches; looser word-set matching produced wrong pairs.
const DEPT_ROLE_WORDS = new Set([
  'REV', 'REVENUE', 'REVENUES', 'INCOME', 'INC',
  'EXP', 'EXPENSE', 'EXPENSES', 'COST', 'COSTS', 'COS', 'COGS',
]);

/** Stripped-stem department key: uppercased, punctuation-collapsed, role words removed. */
function deptStemKey(d) {
  const words = String(d || '')
    .toUpperCase()
    .replace(/[^A-Z0-9 &]/g, ' ')
    .split(/\s+/)
    .filter((w) => w && !DEPT_ROLE_WORDS.has(w));
  return words.join(' ');
}

/** null on null/0/non-finite denominator (DAX DIVIDE semantics). */
/** Percent units (0–100) of num ÷ den, null when undefined. */
function pct(num, den) {
  const r = ratio(num, den);
  return r === null ? null : r * 100;
}

function ratio(num, den) {
  if (num === null || num === undefined) return null;
  const d = Number(den);
  if (!Number.isFinite(d) || d === 0) return null;
  return Number(num) / d;
}

const acctNumKey = (n) => String(n || '').trim();

/**
 * Index the drag-set department presentation order (data.departmentOrders,
 * P&L Mapping tab) by chartOfAccountDepartmentId and by department NAME —
 * leaves routinely carry the department string without the entity id.
 * `forRow(deptId, name)` returns the order, or null when the department is
 * unordered: unordered departments keep the account-number fallback AFTER
 * every ordered one.
 */
function buildDeptOrderIndex(data) {
  // Two levels: org-wide rows (locationId null) and per-location override
  // rows. A location with ANY override rows uses ITS OWN order exclusively;
  // locations without overrides inherit the org rows. Key 'org' holds the
  // org level; numeric keys hold per-location levels.
  const levels = new Map(); // levelKey -> { byId, byName }
  const levelFor = (key) => {
    let level = levels.get(key);
    if (!level) {
      level = { byId: new Map(), byName: new Map() };
      levels.set(key, level);
    }
    return level;
  };
  (data?.departmentOrders || []).forEach((o) => {
    const order = Number(o.displayOrder);
    if (!Number.isFinite(order)) return;
    const level = levelFor(o.locationId == null ? 'org' : Number(o.locationId));
    if (o.chartOfAccountDepartmentId != null) level.byId.set(o.chartOfAccountDepartmentId, order);
    const nameKey = String(o.name || '').trim().toUpperCase();
    if (nameKey && !level.byName.has(nameKey)) level.byName.set(nameKey, order);
  });
  return {
    hasAny: levels.size > 0,
    forRow(locationId, deptId, name) {
      const level = (locationId != null && levels.get(Number(locationId))) || levels.get('org');
      if (!level) return null;
      if (deptId != null && level.byId.has(deptId)) return level.byId.get(deptId);
      const nameKey = String(name || '').trim().toUpperCase();
      return level.byName.has(nameKey) ? level.byName.get(nameKey) : null;
    },
  };
}

/**
 * Ordered-first comparator: departments with a drag-set order lead (ascending
 * displayOrder), everything unordered follows via `fallback`.
 */
function compareByDeptOrder(orderA, orderB, fallback) {
  const aHas = orderA !== null && orderA !== undefined;
  const bHas = orderB !== null && orderB !== undefined;
  if (aHas !== bHas) return aHas ? -1 : 1;
  if (aHas && orderA !== orderB) return orderA - orderB;
  return fallback();
}

/**
 * Index the % Rev / GOP pairing links (data.revenueBasisLinks) by source:
 * account id → Set(revenue accountNumber), department id → Set(number).
 */
export function buildLinkIndex(data) {
  const byAccount = new Map();
  const byDept = new Map();
  const allTargets = new Set();
  (data?.revenueBasisLinks || []).forEach((l) => {
    const num = acctNumKey(l.revenueAccountNumber);
    if (!num) return;
    allTargets.add(num);
    if (l.chartOfAccountId != null) {
      if (!byAccount.has(l.chartOfAccountId)) byAccount.set(l.chartOfAccountId, new Set());
      byAccount.get(l.chartOfAccountId).add(num);
    } else if (l.chartOfAccountDepartmentId != null) {
      if (!byDept.has(l.chartOfAccountDepartmentId)) byDept.set(l.chartOfAccountDepartmentId, new Set());
      byDept.get(l.chartOfAccountDepartmentId).add(num);
    }
  });
  return {
    // Effective link targets for a leaf: account override, else department.
    forRow: (r) => byAccount.get(r.accountId) || (r.chartOfAccountDepartmentId != null ? byDept.get(r.chartOfAccountDepartmentId) : undefined) || null,
    allTargets,
  };
}

/**
 * Department statement captions (chartOfAccountDepartments.presentationName,
 * carried on data.usaliSections). A caption is the line a department PRINTS
 * on, held apart from its ledger name; departments sharing one collapse onto
 * a single statement line (Food + Beverage + Banquet → "Food & Beverage").
 *
 * Resolution mirrors the section overrides: chartOfAccountDepartmentId first,
 * then department NAME, because leaves routinely carry the department string
 * without the entity id.
 */
function buildDeptCaptionIndex(data) {
  const byId = new Map();
  const byName = new Map();
  const nameKey = (n) => String(n || '').trim().toUpperCase();
  (data?.usaliSections || []).forEach((s) => {
    const caption = String(s.presentationName || '').trim();
    if (!caption) return;
    if (s.chartOfAccountDepartmentId != null) byId.set(s.chartOfAccountDepartmentId, caption);
    const key = nameKey(s.name);
    if (key && !byName.has(key)) byName.set(key, caption);
  });
  const captionFor = (r) =>
    (r?.chartOfAccountDepartmentId != null ? byId.get(r.chartOfAccountDepartmentId) : undefined) ??
    byName.get(nameKey(r?.department)) ??
    null;
  return {
    any: byId.size > 0 || byName.size > 0,
    captionFor,
    /** The stem a row groups and measures under: its caption's, else its own. */
    stemFor: (r) => deptStemKey(captionFor(r) || r?.department),
  };
}

/**
 * Index revenue (accountType REVENUE, display-normalized actuals) by account
 * number / stripped department stem / location / portfolio, and room counts
 * by location / portfolio, each at month + 'total' grain — the denominators
 * for % Rev / $POR / $PAR.
 */
function buildMetricIndex(data) {
  // Each map holds { a, b }: actual and budget revenue. % Rev divides actual
  // by actual revenue; the summary statement also needs budget % of budget
  // revenue, so both measures accumulate side by side.
  const revNum = new Map(); // `${loc}|${acctNum}|${month|total}` → revenue
  const revStem = new Map(); // `${loc}|${stem}|${month|total}`
  const revLoc = new Map(); // `${loc}|${month|total}`
  const revPort = new Map(); // `${month|total}`
  const add2 = (map, key, a, b) => {
    const cur = map.get(key) || { a: 0, b: 0 };
    cur.a += a;
    cur.b += b;
    map.set(key, cur);
  };
  // A captioned department's revenue is indexed under BOTH its own stem and
  // its caption's, so a merged unit ("Food & Beverage") measures against the
  // merged revenue while account-level rows keep their own department base.
  const captionIdx = buildDeptCaptionIndex(data);
  (data?.rows || []).forEach((r) => {
    if (normType(r.accountType) !== REVENUE_TYPE) return;
    const amt = Number(r.actual) || 0;
    const bud = Number(r.budget) || 0;
    const stems = new Set([deptStemKey(r.department), captionIdx.stemFor(r)].filter(Boolean));
    const num = acctNumKey(r.accountNumber);
    const m = String(r.month).slice(0, 10);
    if (num) {
      add2(revNum, `${r.locationId}|${num}|${m}`, amt, bud);
      add2(revNum, `${r.locationId}|${num}|total`, amt, bud);
    }
    stems.forEach((stem) => {
      add2(revStem, `${r.locationId}|${stem}|${m}`, amt, bud);
      add2(revStem, `${r.locationId}|${stem}|total`, amt, bud);
    });
    add2(revLoc, `${r.locationId}|${m}`, amt, bud);
    add2(revLoc, `${r.locationId}|total`, amt, bud);
    add2(revPort, m, amt, bud);
    add2(revPort, 'total', amt, bud);
  });

  const rooms = new Map(); // `${loc}|${m}` and `P|${m}` → { occ, avail, budgetOcc, budgetAvail }
  const roomsAdd = (key, r) => {
    const cur = rooms.get(key) || { occ: 0, avail: 0, budgetOcc: 0, budgetAvail: 0 };
    cur.occ += Number(r.actualOccupied) || 0;
    cur.avail += Number(r.actualAvailable) || 0;
    cur.budgetOcc += Number(r.budgetOccupied) || 0;
    cur.budgetAvail += Number(r.budgetAvailable) || 0;
    rooms.set(key, cur);
  };
  (data?.roomStats?.byLocationMonth || []).forEach((r) => {
    const m = String(r.month).slice(0, 10);
    roomsAdd(`${r.locationId}|${m}`, r);
    roomsAdd(`${r.locationId}|total`, r);
    roomsAdd(`P|${m}`, r);
    roomsAdd('P|total', r);
  });

  const pick = (entry, measure) => (entry === undefined ? null : entry[measure]);
  const locBase = (loc, m, measure = 'a') => pick(revLoc.get(`${loc}|${m}`), measure);
  return {
    // Precedence: linked revenue accounts (resolved by number at the row's
    // location) → stripped-name dept revenue → location revenue.
    // `measure` = 'a' (actual, default) | 'b' (budget).
    base: (loc, { linkNums, dept } = {}, m, measure = 'a') => {
      if (linkNums && linkNums.size > 0) {
        let sum = 0;
        let found = false;
        linkNums.forEach((num) => {
          const v = revNum.get(`${loc}|${num}|${m}`);
          if (v !== undefined) {
            sum += v[measure];
            found = true;
          }
        });
        if (found) return sum;
      }
      const stem = deptStemKey(dept);
      if (stem) {
        const s = revStem.get(`${loc}|${stem}|${m}`);
        if (s !== undefined) return s[measure];
      }
      return locBase(loc, m, measure);
    },
    locBase,
    portfolioBase: (m, measure = 'a') => pick(revPort.get(m), measure),
    locRooms: (loc, m) => rooms.get(`${loc}|${m}`) || null,
    portfolioRooms: (m) => rooms.get(`P|${m}`) || null,
  };
}

/**
 * Write the metric cells for one suffix off the row's actual amount. % cells
 * are PERCENT UNITS (69.3 → "69.3%", the formatters contract) so CSV exports
 * read naturally.
 */
function setMetricCells(cells, suffix, amount, revenueBase, rooms, budgetAmount, budgetBase) {
  cells[`pr|${suffix}`] = pct(amount, revenueBase);
  cells[`por|${suffix}`] = rooms ? ratio(amount, rooms.occ) : null;
  cells[`par|${suffix}`] = rooms ? ratio(amount, rooms.avail) : null;
  // Budget as a % of budget revenue — read by the summary statement only.
  if (budgetAmount !== undefined) cells[`pb|${suffix}`] = pct(budgetAmount, budgetBase);
}

/**
 * Group each location's leaves into USALI operating units. A unit is a
 * stripped-stem department ("ROOMS REVENUE" + "Rooms Expenses" → one "Rooms"
 * unit); an expense with revenue-account links joins the unit that owns its
 * linked revenue instead of its own stem (the explicit override, same
 * precedence % Rev uses).
 *
 * Unit section: any member department's explicit usaliSection
 * (data.usaliSections, USALI_PRECEDENCE on conflict); otherwise inferred —
 * has REVENUE-type activity → Operated, only Other Income/Expense types →
 * Non-operating, else Undistributed.
 *
 * Returns locationId → Map(unitKey → { key, label, section, rows, deptNames,
 * sortNumber }). Every leaf lands in exactly one unit.
 */
/**
 * Exported for the Forecasting workspace's GOP layout, which regroups its
 * own department rows with these same pairing and section rules.
 * @param {any} data
 * @param {any} linkIdx
 * @returns {Map<any, Map<string, any>>} locationId → unitKey → unit
 */
export function buildUsaliUnits(data, linkIdx) {
  const deptOrderIdx = buildDeptOrderIndex(data);
  // Overrides resolve by the leaf's chartOfAccountDepartmentId first, then by
  // department NAME — charts routinely carry the department string on
  // accounts without the entity id, and each location's leaves name their own
  // departments, so name matching stays location-correct on orgs whose
  // locations use different charts.
  const sectionByDeptId = new Map();
  const sectionByDeptName = new Map();
  const deptNameKey = (n) => String(n || '').trim().toUpperCase();
  (data?.usaliSections || []).forEach((s) => {
    const section = String(s.usaliSection || '').toUpperCase();
    if (!USALI_PRECEDENCE.includes(section)) return;
    if (s.chartOfAccountDepartmentId != null) sectionByDeptId.set(s.chartOfAccountDepartmentId, section);
    const nameKey = deptNameKey(s.name);
    if (nameKey && !sectionByDeptName.has(nameKey)) sectionByDeptName.set(nameKey, section);
  });

  const captionIdx = buildDeptCaptionIndex(data);
  const explicitSection = (r) =>
    (r?.chartOfAccountDepartmentId != null ? sectionByDeptId.get(r.chartOfAccountDepartmentId) : undefined) ??
    sectionByDeptName.get(deptNameKey(r?.department)) ??
    '';
  /**
   * A unit has two identifiers. `stem` is what it MEASURES under (its %Rev
   * revenue base). `key` is what it MERGES under — for a captioned department
   * the explicit section joins the key, so a statement may print "Other" both
   * as an operated department and as a below-EBITDA line without the two
   * collapsing into one unit.
   */
  const unitRefFor = (r) => {
    const stem = captionIdx.stemFor(r);
    return { stem, key: captionIdx.captionFor(r) ? `${explicitSection(r)}::${stem}` : stem };
  };

  // Revenue accountNumber → owning department stem, per location — resolves
  // an expense's link target into the operated unit that owns the revenue.
  const stemByAcctNum = new Map();
  (data?.rows || []).forEach((r) => {
    if (normType(r.accountType) !== REVENUE_TYPE) return;
    const num = acctNumKey(r.accountNumber);
    if (!num) return;
    const key = `${r.locationId}|${num}`;
    if (!stemByAcctNum.has(key)) stemByAcctNum.set(key, unitRefFor(r));
  });

  const byLocation = new Map();
  (data?.rows || []).forEach((r) => {
    const type = normType(r.accountType);
    let ref = unitRefFor(r);
    if (!INCOME_TYPES.has(type)) {
      const linkNums = linkIdx.forRow(r);
      if (linkNums) {
        for (const num of linkNums) {
          const target = stemByAcctNum.get(`${r.locationId}|${num}`);
          if (target !== undefined) {
            ref = target;
            break;
          }
        }
      }
    }
    const { stem, key: unitKey } = ref;

    let units = byLocation.get(r.locationId);
    if (!units) {
      units = new Map();
      byLocation.set(r.locationId, units);
    }
    let unit = units.get(unitKey);
    if (!unit) {
      unit = {
        key: unitKey,
        // The revenue base this unit measures against; a caption merges the
        // member departments' revenue under the caption's stem too.
        stem,
        // titleCase(stem) drops the punctuation deptStemKey collapsed, so a
        // caption is used verbatim; it lands below once a leaf that actually
        // carries one is seen (a linked expense can open the unit first).
        label: stem ? titleCase(stem) : '(No department)',
        overrides: new Set(),
        hasRevenue: false,
        allNonOperating: true,
        rows: [],
        deptNames: new Set(),
        sortNumber: undefined,
      };
      units.set(unitKey, unit);
    }
    unit.rows.push(r);
    const caption = captionIdx.captionFor(r);
    if (caption && deptStemKey(caption) === stem) unit.label = caption;
    if (type === REVENUE_TYPE) unit.hasRevenue = true;
    if (!NON_OPERATING_TYPES.has(type)) unit.allNonOperating = false;
    if (r.department && String(r.department).trim()) unit.deptNames.add(String(r.department).trim());
    const override =
      (r.chartOfAccountDepartmentId != null ? sectionByDeptId.get(r.chartOfAccountDepartmentId) : undefined) ??
      sectionByDeptName.get(deptNameKey(r.department));
    if (override) unit.overrides.add(override);
    const num = String(r.accountNumber || '');
    if (num && (unit.sortNumber === undefined || num.localeCompare(unit.sortNumber, undefined, { numeric: true }) < 0)) {
      unit.sortNumber = num;
    }
    // A unit merges its member departments (Rooms Revenue + Rooms Expenses);
    // its drag-set order is the lowest member order.
    const explicitOrder = deptOrderIdx.forRow(r.locationId, r.chartOfAccountDepartmentId, r.department);
    if (explicitOrder != null && (unit.displayOrder == null || explicitOrder < unit.displayOrder)) {
      unit.displayOrder = explicitOrder;
    }
  });

  byLocation.forEach((units) => {
    units.forEach((unit) => {
      unit.section =
        USALI_PRECEDENCE.find((s) => unit.overrides.has(s)) ||
        (NON_OPERATING_NAME.test(unit.label || '')
          ? USALI_SECTIONS.NON_OPERATING
          : unit.hasRevenue
            ? USALI_SECTIONS.OPERATED
            : unit.allNonOperating
              ? USALI_SECTIONS.NON_OPERATING
              : USALI_SECTIONS.UNDISTRIBUTED);
    });
  });
  return byLocation;
}

export function titleCase(s) {
  return String(s || '')
    .toLowerCase()
    .replace(/\b([a-z])/g, (m, c) => c.toUpperCase());
}

function parseIsoMonth(value) {
  const [y, m, d = '1'] = String(value || '').slice(0, 10).split('-').map(Number);
  return new Date(y || 1970, (m || 1) - 1, d || 1);
}

function monthKey(month) {
  if (typeof month === 'string') return month.length === 7 ? `${month}-01` : month.slice(0, 10);
  const year = month?.year;
  const monthNum = month?.month;
  if (year && monthNum) return `${year}-${String(monthNum).padStart(2, '0')}-01`;
  return String(month?.key || month?.label || '');
}

function monthLabelFromKey(key, spanYears) {
  return format(parseIsoMonth(key), spanYears ? 'MMM yyyy' : 'MMMM');
}

function setCells(cells, suffix, actual, budget, variance, forecast) {
  cells[`a|${suffix}`] = actual ?? null;
  cells[`b|${suffix}`] = budget ?? null;
  cells[`v|${suffix}`] = variance ?? null;
  cells[`f|${suffix}`] = forecast ?? null;
}

function accumulate(tot, actual, budget, variance, forecast) {
  tot.a = addNullable(tot.a, actual);
  tot.b = addNullable(tot.b, budget);
  tot.v = addNullable(tot.v, variance);
  tot.f = addNullable(tot.f, forecast);
}

function buildColumns({ monthEntries, showBudget = true, actualCellRender, metrics = false, showForecast = false, glCode = false }) {
  const columns = [];
  // GL Code rides the facet picker like the metrics but lives in its own
  // (unlabeled) group ahead of the months — one code per account, not one per
  // month — so toggling it on inserts a single column right after the row
  // header. defaultOff keeps the statement unchanged until the user opts in.
  if (glCode) {
    columns.push({
      key: 'glCode',
      label: 'GL Code',
      group: '',
      facet: 'glCode',
      format: 'text',
      align: 'left',
      defaultOff: true,
      description: 'The GL account number, shown on account rows. Toggle on to read or export the statement with account codes.',
    });
  }
  // `facet` collapses the column picker to one control per metric (Actual /
  // Budget / Var) that applies across every month group + Total, instead of a
  // separate "June · Actual", "Total · Var" entry per (group × metric) pair.
  const pushGroup = (group, suffix) => {
    columns.push({
      key: `a|${suffix}`,
      label: 'Actual',
      group,
      facet: 'actual',
      format: 'number',
      description: 'Posted general-ledger activity for the period, shown positive for both revenue and expenses.',
      ...(actualCellRender ? { render: actualCellRender(suffix) } : {}),
    });
    if (showBudget) {
      columns.push({ key: `b|${suffix}`, label: 'Budget', group, facet: 'budget', variant: 'budget', format: 'number', description: 'Budget for the period from the budget table — imported, or written by publishing a budget version in Forecasting.' });
      columns.push({ key: `v|${suffix}`, label: 'Var', group, facet: 'variance', variant: 'variance', format: 'number', description: 'Actual vs Budget, favorable-oriented: revenue over budget and expenses under budget both show positive.' });
    }
    // Published forecast (financialForecast via the forecast-v3 publish
    // bridge). Opt-in via the column picker — defaultOff keeps the statement
    // shape unchanged until the user enables it.
    if (showForecast) {
      columns.push({ key: `f|${suffix}`, label: 'Forecast', group, facet: 'forecast', variant: 'budget', format: 'number', defaultOff: true, description: 'Latest published forecast for the period — updated each time a forecast version is published in Forecasting.' });
    }
    // Metric columns ride the facet picker like Actual/Budget/Var but start
    // hidden (defaultOff) so the statement keeps its familiar shape until the
    // user opts in. Only the report matrix carries them — the AI-artifact
    // matrix has no roomStats/department data to fill them from.
    if (metrics) {
      columns.push({ key: `pr|${suffix}`, label: '% Rev', group, facet: 'pctRev', format: 'percent1', defaultOff: true, description: "The row's actual as a percent of its revenue base: linked revenue accounts when mapped, else the matching revenue department, else the property's total revenue." });
      columns.push({ key: `por|${suffix}`, label: '$POR', group, facet: 'por', format: 'number2', defaultOff: true, description: 'Per occupied room night: the row’s actual ÷ occupied rooms for the property and period (from room-stat mappings).' });
      columns.push({ key: `par|${suffix}`, label: '$PAR', group, facet: 'par', format: 'number2', defaultOff: true, description: 'Per available room night: the row’s actual ÷ available rooms for the property and period (from room-stat mappings).' });
    }
  };
  monthEntries.forEach((m) => pushGroup(m.label, m.key));
  pushGroup('Total', 'total');
  return columns;
}

/** Sorted month keys + display entries across totals and leaf rows. */
function collectMonthEntries(data) {
  const monthSet = new Set();
  (data.totals?.byMonth || []).forEach((m) => monthSet.add(m.month));
  (data.rows || []).forEach((r) => monthSet.add(r.month));
  (data.statistics?.rows || []).forEach((r) => monthSet.add(r.month));
  const months = [...monthSet].sort();
  const spanYears = months.length > 1 && months[0].slice(0, 4) !== months[months.length - 1].slice(0, 4);
  return { months, monthEntries: months.map((m) => ({ key: m, label: monthLabelFromKey(m, spanYears) })) };
}

// ---------------------------------------------------------------------------
// Statistics section (data.statistics.rows — stat GL accounts tagged with
// their Revenue Map → Room Stats mappedType). Renders above Revenue per
// location: Total Occupied Rooms / Total Available Rooms / Occupancy %
// (computed) / other mapped types / Other Statistics (unmapped accounts),
// each metric expanding to its GL account leaves (drillable). Counts, not
// currency — never folded into the monetary totals, and the % Rev / $POR /
// $PAR facets stay empty on these rows.
// ---------------------------------------------------------------------------

// The occupancy pair leads the section; remaining mapped types follow A→Z.
const STAT_LEAD_TYPES = ['Occupied Rooms', 'Available Rooms'];

/**
 * Statistics rows for one location, ready to splice under the location row.
 * Values are operation-signed (subtract ⇒ negated) and sign-normalized for
 * display per metric: when a metric's period total lands negative (credit-
 * convention stat postings), the whole metric — leaves included — flips
 * positive so leaves keep tying to their metric row.
 */
function buildStatisticsRows(statRows, loc, locRowId) {
  const mine = statRows.filter((r) => r.locationId === loc.id);
  if (mine.length === 0) return [];

  // metricKey ('' = unmapped bucket) → { label, months: Map, tot, accounts: Map }
  const metricsByType = new Map();
  mine.forEach((r) => {
    const key = r.mappedType || '';
    let metric = metricsByType.get(key);
    if (!metric) {
      metric = { mappedType: r.mappedType || null, months: new Map(), tot: { a: null, b: null, f: null }, accounts: new Map() };
      metricsByType.set(key, metric);
    }
    const sign = r.operation === 'subtract' ? -1 : 1;
    const a = r.actual === null || r.actual === undefined ? null : sign * Number(r.actual);
    const b = r.budget === null || r.budget === undefined ? null : sign * Number(r.budget);
    const f = r.forecast === null || r.forecast === undefined ? null : sign * Number(r.forecast);

    let dm = metric.months.get(r.month);
    if (!dm) {
      dm = { a: null, b: null, f: null };
      metric.months.set(r.month, dm);
    }
    dm.a = addNullable(dm.a, a);
    dm.b = addNullable(dm.b, b);
    dm.f = addNullable(dm.f, f);
    metric.tot.a = addNullable(metric.tot.a, a);
    metric.tot.b = addNullable(metric.tot.b, b);
    metric.tot.f = addNullable(metric.tot.f, f);

    let acct = metric.accounts.get(r.chartOfAccountId);
    if (!acct) {
      acct = {
        id: r.chartOfAccountId,
        name: r.accountName || r.accountNumber || `Account ${r.chartOfAccountId}`,
        number: r.accountNumber || '',
        department: r.department || null,
        months: new Map(),
        tot: { a: null, b: null, f: null },
      };
      metric.accounts.set(r.chartOfAccountId, acct);
    }
    let am = acct.months.get(r.month);
    if (!am) {
      am = { a: null, b: null, f: null };
      acct.months.set(r.month, am);
    }
    am.a = addNullable(am.a, a);
    am.b = addNullable(am.b, b);
    am.f = addNullable(am.f, f);
    acct.tot.a = addNullable(acct.tot.a, a);
    acct.tot.b = addNullable(acct.tot.b, b);
    acct.tot.f = addNullable(acct.tot.f, f);
  });

  const bandRowId = `${locRowId}:STATS`;

  // Actual / budget / forecast can each arrive under their own posting
  // convention (e.g. GL stat actuals debit-positive while budget imports
  // store stat rows as credits), so each measure normalizes independently:
  // when a metric's period total for that measure is negative, that measure
  // flips across the metric AND its leaves — leaves keep tying per measure.
  const signsOf = (tot) => ({
    a: (tot.a ?? 0) < 0 ? -1 : 1,
    b: (tot.b ?? 0) < 0 ? -1 : 1,
    f: (tot.f ?? 0) < 0 ? -1 : 1,
  });
  const flip = (v, s) => (v === null || v === undefined ? null : s * v);
  const cellsFor = (entry, signs) => {
    const cells = {};
    const put = (suffix, e) => {
      const a = flip(e.a, signs.a);
      const b = flip(e.b, signs.b);
      cells[`a|${suffix}`] = a;
      cells[`b|${suffix}`] = b;
      cells[`v|${suffix}`] = a === null || b === null ? null : a - b;
      cells[`f|${suffix}`] = flip(e.f, signs.f);
    };
    entry.months.forEach((e, month) => put(month, e));
    put('total', entry.tot);
    return cells;
  };

  // Occupancy % = Occupied ÷ Available per month (total = Σocc/Σavail), in
  // percent units; variance in points. Null when either side is unmapped.
  const occupancyCells = (() => {
    const occ = metricsByType.get('Occupied Rooms');
    const avail = metricsByType.get('Available Rooms');
    if (!occ || !avail) return null;
    const occSigns = signsOf(occ.tot);
    const availSigns = signsOf(avail.tot);
    const cells = {};
    const put = (suffix, o, av) => {
      const aPct = ratio(flip(o?.a, occSigns.a), flip(av?.a, availSigns.a));
      const bPct = ratio(flip(o?.b, occSigns.b), flip(av?.b, availSigns.b));
      const fPct = ratio(flip(o?.f, occSigns.f), flip(av?.f, availSigns.f));
      cells[`a|${suffix}`] = aPct === null ? null : aPct * 100;
      cells[`b|${suffix}`] = bPct === null ? null : bPct * 100;
      cells[`v|${suffix}`] = aPct === null || bPct === null ? null : (aPct - bPct) * 100;
      cells[`f|${suffix}`] = fPct === null ? null : fPct * 100;
    };
    const suffixes = new Set([...occ.months.keys(), ...avail.months.keys()]);
    suffixes.forEach((month) => put(month, occ.months.get(month), avail.months.get(month)));
    put('total', occ.tot, avail.tot);
    return cells;
  })();

  // The band row reads as Occupancy % at a glance — the headline stat —
  // when the occupied/available pair is mapped; otherwise it's a bare header
  // (mixed units make any other band-level sum meaningless).
  const rows = [{
    id: bandRowId,
    parentId: locRowId,
    label: occupancyCells ? 'Statistics · Occ %' : 'Statistics',
    cells: occupancyCells ? { ...occupancyCells } : {},
    ...(occupancyCells ? { format: 'percent1' } : {}),
    variant: 'subtotal',
    stmt: { kind: 'statsBand' },
  }];

  const pushMetric = (metric, rowId, label) => {
    const signs = signsOf(metric.tot);
    rows.push({ id: rowId, parentId: bandRowId, label, cells: cellsFor(metric, signs) });
    const sortedAccounts = [...metric.accounts.values()].sort(
      (a, b) => a.number.localeCompare(b.number, undefined, { numeric: true }) || a.name.localeCompare(b.name)
    );
    sortedAccounts.forEach((acct) => {
      rows.push({
        id: `${rowId}:A${acct.id}`,
        parentId: rowId,
        label: acct.department ? `${acct.name} — ${titleCase(acct.department)}` : acct.name,
        cells: { ...cellsFor(acct, signs), ...(acct.number ? { glCode: acct.number } : {}) },
        drill: {
          accountId: acct.id,
          accountNumber: acct.number,
          department: acct.department || null,
          accountLabel: `${acct.number ? `${acct.number} · ` : ''}${acct.name}`,
          locationId: loc.id,
        },
      });
    });
  };

  const mappedKeys = [...metricsByType.keys()].filter((k) => k !== '');
  const orderedKeys = [
    ...STAT_LEAD_TYPES.filter((k) => metricsByType.has(k)),
    ...mappedKeys.filter((k) => !STAT_LEAD_TYPES.includes(k)).sort((a, b) => a.localeCompare(b)),
  ];

  const emitOccupancy = () => {
    if (!occupancyCells) return;
    rows.push({ id: `${bandRowId}:OCC`, parentId: bandRowId, label: 'Occupancy %', cells: { ...occupancyCells }, format: 'percent1' });
  };

  orderedKeys.forEach((key) => {
    pushMetric(metricsByType.get(key), `${bandRowId}:M${key}`, `Total ${titleCase(key)}`);
    // Occupancy % reads right under the pair that defines it.
    if (key === 'Available Rooms') emitOccupancy();
  });
  if (!metricsByType.has('Available Rooms')) emitOccupancy();

  // Unmapped stat accounts: listed individually — mixed units (guests, covers,
  // rooms…) make a bucket sum meaningless, so the group row carries no cells.
  const other = metricsByType.get('');
  if (other) {
    const otherRowId = `${bandRowId}:OTHER`;
    rows.push({ id: otherRowId, parentId: bandRowId, label: 'Other Statistics', cells: {} });
    const sortedAccounts = [...other.accounts.values()].sort(
      (a, b) => a.number.localeCompare(b.number, undefined, { numeric: true }) || a.name.localeCompare(b.name)
    );
    sortedAccounts.forEach((acct) => {
      rows.push({
        id: `${otherRowId}:A${acct.id}`,
        parentId: otherRowId,
        label: acct.department ? `${acct.name} — ${titleCase(acct.department)}` : acct.name,
        cells: { ...cellsFor(acct, signsOf(acct.tot)), ...(acct.number ? { glCode: acct.number } : {}) },
        drill: {
          accountId: acct.id,
          accountNumber: acct.number,
          department: acct.department || null,
          accountLabel: `${acct.number ? `${acct.number} · ` : ''}${acct.name}`,
          locationId: loc.id,
        },
      });
    });
  }

  return rows;
}

/**
 * Fill % Rev / $POR / $PAR for every amount cell already on the row. `ref`
 * ({ linkNums?, dept? }) selects the % Rev base via the precedence chain
 * (links → stripped-name dept → location revenue); null means location
 * revenue. Rooms are always location-scoped (property-level occupancy).
 */
function makeMetricFiller(metricIdx, allSuffixes) {
  return (cells, locId, ref) => {
    allSuffixes.forEach((m) => {
      if (cells[`a|${m}`] === undefined) return;
      const base = ref ? metricIdx.base(locId, ref, m) : metricIdx.locBase(locId, m);
      const budgetBase = ref ? metricIdx.base(locId, ref, m, 'b') : metricIdx.locBase(locId, m, 'b');
      setMetricCells(cells, m, cells[`a|${m}`], base, metricIdx.locRooms(locId, m), cells[`b|${m}`], budgetBase);
    });
  };
}

// ---------------------------------------------------------------------------
// Consolidated view — drop the Location tier and read every property as one
// statement. A pure transform on the financialPnl payload rather than a mode
// inside each builder, so the P&L / Departments / GOP views, the
// exports and the PDF all consolidate identically and for free.
//
// Merge key is the GL ACCOUNT NUMBER (falling back to the account name)
// within its account type + department — sibling properties on one chart
// share numbers while their `accountId`s differ per location. Drill payloads
// keep every contributing (locationId, accountId) pair so a consolidated line
// still opens its transactions across all the properties behind it.
// ---------------------------------------------------------------------------

export const CONSOLIDATED_LOCATION_ID = 0;

/**
 * Union the (locationId, accountId) sources a consolidated leaf came from
 * onto the account bucket being built, so its GL drill can fan out across
 * every property behind the line. A no-op on unconsolidated payloads.
 */
function mergeLeafSources(acct, r) {
  if (!r.sourceLocationIds && !r.sourceAccountIds) return;
  if (!acct.srcLocs) acct.srcLocs = new Set();
  if (!acct.srcAccts) acct.srcAccts = new Set();
  (r.sourceLocationIds || []).forEach((v) => acct.srcLocs.add(v));
  (r.sourceAccountIds || []).forEach((v) => acct.srcAccts.add(v));
}

/** Drill payload extras for a consolidated leaf (empty when not consolidated). */
function leafSourceDrill(acct) {
  if (!acct.srcLocs || acct.srcLocs.size === 0) return {};
  return { locationIds: [...acct.srcLocs], accountIds: [...acct.srcAccts] };
}

const consolidatedKey = (r) =>
  `${normType(r.accountType)}|${String(r.department || '').trim().toUpperCase()}|${
    String(r.accountNumber || '').trim() || String(r.accountName || r.accountId || '').trim().toUpperCase()
  }`;

/** Sum a measure across duplicates, treating absent as "no contribution". */
const sumInto = (target, source, keys) => {
  keys.forEach((k) => {
    target[k] = addNullable(target[k], source[k]);
  });
};

/**
 * Collapse a financialPnl payload onto one synthetic location.
 * @param {Object} data - the payload as fetched
 * @param {string} [label] - the consolidated row's name
 * @returns {Object|null} a new payload (input untouched), or the input when
 *   there is nothing to consolidate (no data, or a single property already).
 */
export function consolidateFinancialPnlData(data, label = 'All properties') {
  if (!data) return data;
  const locIds = new Set();
  (data.subtotals?.byLocation || []).forEach((e) => locIds.add(e.locationId));
  (data.rows || []).forEach((r) => locIds.add(r.locationId));
  if (locIds.size <= 1) return data;

  const LOC = { locationId: CONSOLIDATED_LOCATION_ID, locationName: label };
  const MEASURES = ['actual', 'budget', 'variance', 'forecast'];

  // A consolidated leaf must carry the SAME accountId in every month: the
  // matrix keys its account rows on accountId, and a leaf that inherited
  // whichever property happened to be first for that month would split one
  // account into several rows as soon as its contributing properties differ
  // month to month (e.g. a rent account only some properties post in July).
  // Lowest contributing accountId, chosen per merge key across all months.
  const stableAccountId = new Map();
  (data.rows || []).forEach((r) => {
    if (r.accountId == null) return;
    const key = consolidatedKey(r);
    const cur = stableAccountId.get(key);
    if (cur === undefined || r.accountId < cur) stableAccountId.set(key, r.accountId);
  });

  // Account leaves.
  const rowsByKey = new Map();
  (data.rows || []).forEach((r) => {
    const key = `${consolidatedKey(r)}|${r.month}`;
    let out = rowsByKey.get(key);
    if (!out) {
      const stableId = stableAccountId.get(consolidatedKey(r));
      out = {
        ...r,
        ...(stableId === undefined ? {} : { accountId: stableId }),
        ...LOC,
        // Every property behind this line — the drill fans out across them.
        sourceLocationIds: [],
        sourceAccountIds: [],
      };
      MEASURES.forEach((m) => { out[m] = null; });
      rowsByKey.set(key, out);
    }
    sumInto(out, r, MEASURES);
    if (!out.sourceLocationIds.includes(r.locationId)) out.sourceLocationIds.push(r.locationId);
    if (r.accountId != null && !out.sourceAccountIds.includes(r.accountId)) out.sourceAccountIds.push(r.accountId);
    if (out.chartOfAccountDepartmentId == null && r.chartOfAccountDepartmentId != null) {
      out.chartOfAccountDepartmentId = r.chartOfAccountDepartmentId;
    }
    if (Number(r.transactionCount) > 0) {
      out.transactionCount = (Number(out.transactionCount) || 0) + Number(r.transactionCount);
    }
  });

  // Location subtotal → one row per month.
  const byLocation = new Map();
  (data.subtotals?.byLocation || []).forEach((e) => {
    let out = byLocation.get(e.month);
    if (!out) {
      out = { ...e, ...LOC };
      MEASURES.forEach((m) => { out[m] = null; });
      byLocation.set(e.month, out);
    }
    sumInto(out, e, MEASURES);
  });

  // Account-type subtotal → one row per (type, month).
  const byAccountType = new Map();
  (data.subtotals?.byAccountType || []).forEach((e) => {
    const key = `${normType(e.accountType)}|${e.month}`;
    let out = byAccountType.get(key);
    if (!out) {
      out = { ...e, ...LOC };
      MEASURES.forEach((m) => { out[m] = null; });
      byAccountType.set(key, out);
    }
    sumInto(out, e, MEASURES);
  });

  // Statistics leaves — same account-number merge, per mapped type.
  const statByKey = new Map();
  (data.statistics?.rows || []).forEach((r) => {
    const key = `${r.mappedType || ''}|${r.operation || ''}|${String(r.accountNumber || '').trim() || String(r.accountName || r.chartOfAccountId || '').trim().toUpperCase()}|${r.month}`;
    let out = statByKey.get(key);
    if (!out) {
      out = { ...r, ...LOC };
      MEASURES.forEach((m) => { out[m] = null; });
      statByKey.set(key, out);
    }
    sumInto(out, r, MEASURES);
  });

  // Room stats — occupied and available sum, so Occ% / ADR / RevPAR come out
  // as true portfolio ratios rather than an average of averages.
  const roomsByMonth = new Map();
  (data.roomStats?.byLocationMonth || []).forEach((r) => {
    let out = roomsByMonth.get(r.month);
    if (!out) {
      out = { ...r, locationId: CONSOLIDATED_LOCATION_ID };
      ['actualOccupied', 'actualAvailable', 'budgetOccupied', 'budgetAvailable'].forEach((k) => { out[k] = 0; });
      roomsByMonth.set(r.month, out);
    }
    ['actualOccupied', 'actualAvailable', 'budgetOccupied', 'budgetAvailable'].forEach((k) => {
      out[k] += Number(r[k]) || 0;
    });
  });

  return {
    ...data,
    consolidated: true,
    rows: [...rowsByKey.values()],
    subtotals: {
      ...(data.subtotals || {}),
      byLocation: [...byLocation.values()],
      byAccountType: [...byAccountType.values()],
    },
    statistics: data.statistics ? { ...data.statistics, rows: [...statByKey.values()] } : data.statistics,
    roomStats: data.roomStats ? { ...data.roomStats, byLocationMonth: [...roomsByMonth.values()] } : data.roomStats,
  };
}

export function buildFinancialPnlReportMatrix(data, { showBudget = true, actualCellRender } = {}) {
  if (!data) return { columns: [], rows: [], footer: null };

  // detail=summary payloads carry department-grain rows (one synthetic row
  // per location|type|department|month) — the statement renders down to the
  // department level and the account leaves arrive in a background fetch.
  const departmentGrain = data.detailGrain === 'department';

  const { months, monthEntries } = collectMonthEntries(data);
  const columns = buildColumns({ monthEntries, showBudget, actualCellRender, metrics: true, showForecast: true, glCode: true });

  const metricIdx = buildMetricIndex(data);
  const linkIdx = buildLinkIndex(data);
  const deptOrderIdx = buildDeptOrderIndex(data);
  const allSuffixes = [...months, 'total'];
  const fillMetrics = makeMetricFiller(metricIdx, allSuffixes);

  const locs = new Map();
  (data.subtotals?.byLocation || []).forEach((e) => {
    let loc = locs.get(e.locationId);
    if (!loc) {
      loc = { id: e.locationId, name: e.locationName, cells: {}, tot: { a: null, b: null, v: null, f: null }, types: new Map() };
      locs.set(e.locationId, loc);
    }
    setCells(loc.cells, e.month, e.actual, e.budget, e.variance, e.forecast);
    accumulate(loc.tot, e.actual, e.budget, e.variance, e.forecast);
  });

  (data.subtotals?.byAccountType || []).forEach((e) => {
    const loc = locs.get(e.locationId);
    if (!loc) return;
    const typeKey = normType(e.accountType);
    let type = loc.types.get(typeKey);
    if (!type) {
      type = {
        key: typeKey,
        label: titleCase(e.accountType),
        sort: e.accountTypeSort ?? 99,
        cells: {},
        tot: { a: null, b: null, v: null, f: null },
        accounts: new Map(),
      };
      loc.types.set(typeKey, type);
    }
    setCells(type.cells, e.month, e.actual, e.budget, e.variance, e.forecast);
    accumulate(type.tot, e.actual, e.budget, e.variance, e.forecast);
  });

  (data.rows || []).forEach((r) => {
    const loc = locs.get(r.locationId);
    if (!loc) return;
    const type = loc.types.get(normType(r.accountType));
    if (!type) return;
    if (!type.departments) type.departments = new Map();
    const deptName = (r.department && String(r.department).trim()) || '(No department)';
    let dept = type.departments.get(deptName);
    if (!dept) {
      dept = { name: deptName, months: new Map(), tot: { a: null, b: null, v: null, f: null }, accounts: new Map(), deptId: null };
      type.departments.set(deptName, dept);
    }
    if (r.chartOfAccountDepartmentId != null) dept.deptId = r.chartOfAccountDepartmentId;
    // Order departments by their lowest GL account number (see sort below).
    const acctNumber = String(r.accountNumber || '');
    if (acctNumber && (dept.sortNumber === undefined || acctNumber.localeCompare(dept.sortNumber, undefined, { numeric: true }) < 0)) {
      dept.sortNumber = acctNumber;
    }
    // Drag-set presentation order (P&L Mapping tab) trumps the account-number
    // fallback; resolved per row so name-only leaves still pick it up.
    if (dept.displayOrder == null) {
      const explicitOrder = deptOrderIdx.forRow(r.locationId, r.chartOfAccountDepartmentId, r.department);
      if (explicitOrder != null) dept.displayOrder = explicitOrder;
    }
    let dm = dept.months.get(r.month);
    if (!dm) {
      dm = { a: null, b: null, v: null, f: null };
      dept.months.set(r.month, dm);
    }
    accumulate(dm, r.actual, r.budget, r.variance, r.forecast);
    accumulate(dept.tot, r.actual, r.budget, r.variance, r.forecast);

    let acct = dept.accounts.get(r.accountId);
    if (!acct) {
      acct = {
        id: r.accountId,
        label: r.accountName || r.accountNumber || `Account ${r.accountId}`,
        number: r.accountNumber || '',
        linkNums: linkIdx.forRow(r),
        cells: {},
        tot: { a: null, b: null, v: null, f: null },
      };
      dept.accounts.set(r.accountId, acct);
    }
    mergeLeafSources(acct, r);
    setCells(acct.cells, r.month, r.actual, r.budget, r.variance, r.forecast);
    accumulate(acct.tot, r.actual, r.budget, r.variance, r.forecast);
  });

  const rows = [];
  const statRows = data.statistics?.rows || [];
  // Locations with statistics but no monetary rows in scope (e.g. the
  // Account-type filter set to stat types only) still need a location row
  // for the band to hang under — monetary cells stay blank.
  statRows.forEach((r) => {
    if (locs.has(r.locationId)) return;
    locs.set(r.locationId, {
      id: r.locationId,
      name: r.locationName || `Location ${r.locationId}`,
      cells: {},
      tot: { a: null, b: null, v: null, f: null },
      types: new Map(),
    });
  });
  const sortedLocs = [...locs.values()].sort((a, b) => String(a.name).localeCompare(String(b.name)));
  sortedLocs.forEach((loc) => {
    const locRowId = `L${loc.id}`;
    setCells(loc.cells, 'total', loc.tot.a, loc.tot.b, loc.tot.v, loc.tot.f);
    fillMetrics(loc.cells, loc.id, null);
    rows.push({ id: locRowId, label: loc.name, cells: loc.cells, stmt: { kind: 'location', locationId: loc.id }, xf: { id: locRowId, locationId: loc.id, label: loc.name } });

    // Statistics section first — stat counts read above the Revenue band.
    rows.push(...buildStatisticsRows(statRows, loc, locRowId));

    const sortedTypes = [...loc.types.values()].sort((a, b) => a.sort - b.sort || a.label.localeCompare(b.label));
    sortedTypes.forEach((type) => {
      const typeRowId = `${locRowId}:${type.key}`;
      setCells(type.cells, 'total', type.tot.a, type.tot.b, type.tot.v, type.tot.f);
      fillMetrics(type.cells, loc.id, null);
      rows.push({ id: typeRowId, parentId: locRowId, label: type.label, cells: type.cells, variant: 'subtotal', stmt: { kind: 'type', income: INCOME_TYPES.has(type.key) }, xf: { id: typeRowId, locationId: loc.id, label: `${loc.name} · ${type.label}` } });

      // Drag-set departments (P&L Mapping tab) lead in their set order; the
      // rest follow the GL account-number sequence — the department whose
      // accounts are numbered first leads (e.g. Rooms ahead of other
      // departments under both Revenue and Expense) — not alphabetical order.
      const sortedDepts = [...(type.departments?.values() || [])].sort(
        (a, b) => compareByDeptOrder(a.displayOrder, b.displayOrder, () =>
          String(a.sortNumber ?? '').localeCompare(String(b.sortNumber ?? ''), undefined, { numeric: true }) ||
          a.name.localeCompare(b.name))
      );
      sortedDepts.forEach((dept) => {
        const deptRowId = `${typeRowId}:D${dept.name}`;
        const deptCells = {};
        dept.months.forEach((dm, month) => setCells(deptCells, month, dm.a, dm.b, dm.v, dm.f));
        setCells(deptCells, 'total', dept.tot.a, dept.tot.b, dept.tot.v, dept.tot.f);
        const deptLinkNums = dept.deptId != null ? linkIdx.forRow({ accountId: null, chartOfAccountDepartmentId: dept.deptId }) : null;
        fillMetrics(deptCells, loc.id, { dept: dept.name, linkNums: deptLinkNums });
        rows.push({ id: deptRowId, parentId: typeRowId, label: titleCase(dept.name), cells: deptCells, stmt: { kind: 'department' }, xf: { id: deptRowId, locationId: loc.id, department: dept.name === '(No department)' ? null : dept.name, label: `${loc.name} · ${titleCase(dept.name)}` } });

        if (departmentGrain) return; // account leaves land with the full fetch

        const sortedAccounts = [...dept.accounts.values()].sort(
          (a, b) => a.number.localeCompare(b.number) || a.label.localeCompare(b.label)
        );
        sortedAccounts.forEach((acct) => {
          setCells(acct.cells, 'total', acct.tot.a, acct.tot.b, acct.tot.v, acct.tot.f);
          fillMetrics(acct.cells, loc.id, { dept: dept.name, linkNums: acct.linkNums });
          if (acct.number) acct.cells.glCode = acct.number;
          rows.push({ id: `${deptRowId}:A${acct.id}`, parentId: deptRowId, label: acct.label, cells: acct.cells, stmt: { kind: 'account' }, drill: { accountId: acct.id, accountNumber: acct.number, department: dept.name, accountLabel: `${acct.number ? `${acct.number} · ` : ''}${acct.label}`, locationId: loc.id, ...leafSourceDrill(acct) }, xf: { id: `${deptRowId}:A${acct.id}`, locationId: loc.id, department: dept.name === '(No department)' ? null : dept.name, accountId: acct.id, label: `${loc.name} · ${acct.label}` } });
        });
      });
    });
  });

  const footerCells = {};
  (data.totals?.byMonth || []).forEach((m) => setCells(footerCells, m.month, m.actual, m.budget, m.variance, m.forecast));
  const portfolio = data.totals?.portfolio;
  if (portfolio) setCells(footerCells, 'total', portfolio.actual, portfolio.budget, portfolio.variance, portfolio.forecast);
  allSuffixes.forEach((m) => {
    if (footerCells[`a|${m}`] === undefined) return;
    setMetricCells(footerCells, m, footerCells[`a|${m}`], metricIdx.portfolioBase(m), metricIdx.portfolioRooms(m));
  });

  return { columns, rows, footer: { label: 'Portfolio Total', cells: footerCells } };
}

const negate = (v) => (v === null || v === undefined ? v : -v);

/**
 * USALI summary operating statement (the Dept / GOP views): the same P&L
 * leaves regrouped into operating units (buildUsaliUnits) and read in USALI
 * order per location —
 *
 *   operated units (departmental profit each)
 *   Total Operated Department Profit
 *   undistributed units · Total Undistributed Expenses
 *   Gross Operating Profit (GOP)
 *   non-operating units · Net Income
 *
 * Operated units and the profit lines are profit-signed; undistributed and
 * non-operating rows read cost-positive (they subtract), like the printed
 * statement. Variance is favorable-oriented on every leaf, which makes it
 * sign-invariant — it sums straight through both presentations. Every leaf
 * lands in exactly one unit, so Net Income ties to the location total.
 *
 * `detail: true` (Dept view) expands each unit into Revenue / Expenses sides
 * and GL-drillable account leaves; `detail: false` stops at the unit rows.
 *
 * `layout: 'statement'` (GOP view) mirrors the printed operating statement:
 * the operated band splits into a Revenue band and a Department Expense band
 * (one row per operated unit under each, account leaves beneath), keeps the
 * Total Operated Department Profit line between them and the undistributed
 * band, and gives undistributed / non-operating units account drill-downs.
 *
 * `bands: true` wraps each cost band (Undistributed, Management Fees, Fixed
 * Charges, Interest/Depreciation & Other) under a heading row carrying the
 * band total; `stats: true` leads each location with the Statistics block
 * (Rooms Available / Sold / Occ % / ADR / RevPAR). Both feed the Summary
 * Operating Statement (buildFinancialSummaryMatrix).
 */
export function buildFinancialUsaliMatrix(data, { showBudget = true, actualCellRender, detail = false, layout = 'summary', bands = false, stats = false } = {}) {
  if (!data) return { columns: [], rows: [], footer: null };

  const { months, monthEntries } = collectMonthEntries(data);
  const columns = buildColumns({ monthEntries, showBudget, actualCellRender, metrics: true, showForecast: true, glCode: true });
  const metricIdx = buildMetricIndex(data);
  const linkIdx = buildLinkIndex(data);
  const unitsByLocation = buildUsaliUnits(data, linkIdx);
  const allSuffixes = [...months, 'total'];
  const fillMetrics = makeMetricFiller(metricIdx, allSuffixes);

  // month → {a, b, v} accumulators. `sign` flips expenses to profit terms;
  // side/account rows keep the display-normalized positive amounts (sign 1).
  const addSigned = (bucket, r, sign) => {
    let dm = bucket.get(r.month);
    if (!dm) {
      dm = { a: null, b: null, v: null, f: null };
      bucket.set(r.month, dm);
    }
    if (r.actual !== null && r.actual !== undefined) dm.a = (dm.a ?? 0) + sign * Number(r.actual);
    if (r.budget !== null && r.budget !== undefined) dm.b = (dm.b ?? 0) + sign * Number(r.budget);
    if (r.forecast !== null && r.forecast !== undefined) dm.f = (dm.f ?? 0) + sign * Number(r.forecast);
    dm.v = addNullable(dm.v, r.variance);
  };
  const mergeBucket = (into, from) => {
    from.forEach((dm, m) => {
      let t = into.get(m);
      if (!t) {
        t = { a: null, b: null, v: null, f: null };
        into.set(m, t);
      }
      t.a = addNullable(t.a, dm.a);
      t.b = addNullable(t.b, dm.b);
      t.v = addNullable(t.v, dm.v);
      t.f = addNullable(t.f, dm.f);
    });
  };
  // `flip` renders a profit-signed bucket cost-positive; variance stays as-is
  // (favorable-oriented either way).
  const bucketToCells = (bucket, flip = false) => {
    const cells = {};
    const tot = { a: null, b: null, v: null, f: null };
    bucket.forEach((dm, m) => {
      setCells(cells, m, flip ? negate(dm.a) : dm.a, flip ? negate(dm.b) : dm.b, dm.v, flip ? negate(dm.f) : dm.f);
      accumulate(tot, dm.a, dm.b, dm.v, dm.f);
    });
    setCells(cells, 'total', flip ? negate(tot.a) : tot.a, flip ? negate(tot.b) : tot.b, tot.v, flip ? negate(tot.f) : tot.f);
    return cells;
  };

  // Location shells (name + net-income cells), as on the income statement.
  const locs = new Map();
  (data.subtotals?.byLocation || []).forEach((e) => {
    let loc = locs.get(e.locationId);
    if (!loc) {
      loc = { id: e.locationId, name: e.locationName, cells: {}, tot: { a: null, b: null, v: null, f: null } };
      locs.set(e.locationId, loc);
    }
    setCells(loc.cells, e.month, e.actual, e.budget, e.variance, e.forecast);
    accumulate(loc.tot, e.actual, e.budget, e.variance, e.forecast);
  });

  const rows = [];
  const sortedLocs = [...locs.values()].sort((a, b) => String(a.name).localeCompare(String(b.name)));
  sortedLocs.forEach((loc) => {
    const locRowId = `L${loc.id}`;
    setCells(loc.cells, 'total', loc.tot.a, loc.tot.b, loc.tot.v, loc.tot.f);
    fillMetrics(loc.cells, loc.id, null);
    rows.push({ id: locRowId, label: loc.name, cells: loc.cells, xf: { id: locRowId, locationId: loc.id, label: loc.name } });

    const bySection = {};
    USALI_PRECEDENCE.forEach((k) => { bySection[k] = []; });
    (unitsByLocation.get(loc.id) || new Map()).forEach((u) => bySection[u.section].push(u));
    // Drag-set units lead within their USALI band; the rest keep the
    // account-number fallback.
    Object.values(bySection).forEach((list) =>
      list.sort(
        (a, b) => compareByDeptOrder(a.displayOrder, b.displayOrder, () =>
          String(a.sortNumber ?? '').localeCompare(String(b.sortNumber ?? ''), undefined, { numeric: true }) ||
          a.label.localeCompare(b.label))
      )
    );

    const pushAccountLeaves = (sideRows, accountParentId) => {
      const accounts = new Map();
      sideRows.forEach((r) => {
        let acct = accounts.get(r.accountId);
        if (!acct) {
          acct = {
            id: r.accountId,
            label: r.accountName || r.accountNumber || `Account ${r.accountId}`,
            number: r.accountNumber || '',
            dept: r.department,
            linkNums: linkIdx.forRow(r),
            cells: {},
            tot: { a: null, b: null, v: null, f: null },
          };
          accounts.set(r.accountId, acct);
        }
        mergeLeafSources(acct, r);
        setCells(acct.cells, r.month, r.actual, r.budget, r.variance, r.forecast);
        accumulate(acct.tot, r.actual, r.budget, r.variance, r.forecast);
      });
      [...accounts.values()]
        .sort((a, b) => a.number.localeCompare(b.number, undefined, { numeric: true }) || a.label.localeCompare(b.label))
        .forEach((acct) => {
          setCells(acct.cells, 'total', acct.tot.a, acct.tot.b, acct.tot.v, acct.tot.f);
          fillMetrics(acct.cells, loc.id, { dept: acct.dept, linkNums: acct.linkNums });
          if (acct.number) acct.cells.glCode = acct.number;
          rows.push({
            id: `${accountParentId}:A${acct.id}`,
            parentId: accountParentId,
            label: acct.label,
            cells: acct.cells,
            drill: { accountId: acct.id, accountNumber: acct.number, department: acct.dept, accountLabel: `${acct.number ? `${acct.number} · ` : ''}${acct.label}`, locationId: loc.id, ...leafSourceDrill(acct) },
          });
        });
    };

    const pushUnitDetail = (unit, unitRowId) => {
      const sides = [
        { key: 'REV', label: unit.section === USALI_SECTIONS.OPERATED ? 'Revenue' : 'Income', match: (t) => INCOME_TYPES.has(t) },
        { key: 'EXP', label: 'Expenses', match: (t) => !INCOME_TYPES.has(t) },
      ]
        .map((side) => ({ side, sideRows: unit.rows.filter((r) => side.match(normType(r.accountType))) }))
        .filter((s) => s.sideRows.length > 0);

      sides.forEach(({ side, sideRows }) => {
        // Single-sided units (undistributed, typically) hang their accounts
        // straight off the unit row — a lone "Expenses" tier is noise.
        let accountParentId = unitRowId;
        if (sides.length > 1) {
          const sideBucket = new Map();
          sideRows.forEach((r) => addSigned(sideBucket, r, 1));
          const cells = bucketToCells(sideBucket);
          fillMetrics(cells, loc.id, { dept: unit.stem });
          accountParentId = `${unitRowId}:${side.key}`;
          rows.push({ id: accountParentId, parentId: unitRowId, label: side.label, cells, variant: 'subtotal' });
        }
        pushAccountLeaves(sideRows, accountParentId);
      });
    };

    const bandBuckets = {};
    USALI_PRECEDENCE.forEach((k) => { bandBuckets[k] = new Map(); });
    const pushUnit = (unit, flip, parentId = locRowId) => {
      const bucket = new Map();
      unit.rows.forEach((r) => addSigned(bucket, r, INCOME_TYPES.has(normType(r.accountType)) ? 1 : -1));
      mergeBucket(bandBuckets[unit.section], bucket);
      const cells = bucketToCells(bucket, flip);
      fillMetrics(cells, loc.id, { dept: unit.stem });
      const unitRowId = `${locRowId}:U${unit.key || '(none)'}`;
      const xfDept = unit.deptNames.size === 1 ? [...unit.deptNames][0] : null;
      rows.push({
        id: unitRowId,
        parentId,
        label: unit.label,
        cells,
        ...(xfDept
          ? { xf: { id: unitRowId, locationId: loc.id, department: xfDept, label: `${loc.name} · ${unit.label}` } }
          : {}),
      });
      if (detail || layout === 'statement') pushUnitDetail(unit, unitRowId);
    };
    const pushLine = (id, label, bucket, { flip = false, variant = 'emphasis' } = {}) => {
      const cells = bucketToCells(bucket, flip);
      fillMetrics(cells, loc.id, null);
      rows.push({ id: `${locRowId}:${id}`, parentId: locRowId, label, cells, variant });
    };
    // A cost band below the operated departments: its units (cost-positive)
    // and a band total. `bands: true` (summary statement) wraps the units
    // under a band heading row that carries the total — the printed
    // "UNDISTRIBUTED EXPENSE … TOTAL" block as one collapsible node; the
    // default keeps the units flat under the location with the total beneath.
    const pushCostBand = (section, bandId, label) => {
      const units = bySection[section];
      if (units.length === 0) return false;
      if (bands) {
        const bandRowId = `${locRowId}:${bandId}`;
        const at = rows.length;
        units.forEach((u) => pushUnit(u, true, bandRowId));
        const cells = bucketToCells(bandBuckets[section], true);
        fillMetrics(cells, loc.id, null);
        rows.splice(at, 0, { id: bandRowId, parentId: locRowId, label, cells, variant: 'subtotal' });
      } else {
        units.forEach((u) => pushUnit(u, true));
        pushLine(bandId, `Total ${label}`, bandBuckets[section], { flip: true, variant: 'subtotal' });
      }
      return true;
    };

    // Statement layout (GOP view): the operated band reads like the printed
    // statement — a Revenue band and a Department Expense band, one row per
    // operated unit under each with account leaves beneath. Both bands show
    // display-positive amounts; the signed operated bucket (revenue − expense)
    // still feeds Total Operated Department Profit and GOP.
    const pushStatementBands = () => {
      const operated = bySection[USALI_SECTIONS.OPERATED];
      operated.forEach((u) =>
        u.rows.forEach((r) => addSigned(bandBuckets[USALI_SECTIONS.OPERATED], r, INCOME_TYPES.has(normType(r.accountType)) ? 1 : -1))
      );
      [
        { key: 'REVBAND', label: 'Revenue', match: (t) => INCOME_TYPES.has(t) },
        { key: 'EXPBAND', label: 'Department Expense', match: (t) => !INCOME_TYPES.has(t) },
      ].forEach((band) => {
        const unitSides = operated
          .map((u) => ({ u, sideRows: u.rows.filter((r) => band.match(normType(r.accountType))) }))
          .filter((x) => x.sideRows.length > 0);
        if (!unitSides.length) return;
        const bandRowId = `${locRowId}:${band.key}`;
        const bandBucket = new Map();
        const pending = unitSides.map(({ u, sideRows }) => {
          const bucket = new Map();
          sideRows.forEach((r) => addSigned(bucket, r, 1));
          mergeBucket(bandBucket, bucket);
          const cells = bucketToCells(bucket);
          fillMetrics(cells, loc.id, { dept: u.key });
          const rowId = `${bandRowId}:U${u.key || '(none)'}`;
          const xfDept = u.deptNames.size === 1 ? [...u.deptNames][0] : null;
          return {
            rowId,
            sideRows,
            row: {
              id: rowId,
              parentId: bandRowId,
              label: u.label,
              cells,
              ...(xfDept
                ? { xf: { id: rowId, locationId: loc.id, department: xfDept, label: `${loc.name} · ${u.label} ${band.label}` } }
                : {}),
            },
          };
        });
        const bandCells = bucketToCells(bandBucket);
        fillMetrics(bandCells, loc.id, null);
        rows.push({ id: bandRowId, parentId: locRowId, label: band.label, cells: bandCells, variant: 'subtotal' });
        pending.forEach(({ row, rowId, sideRows }) => {
          rows.push(row);
          pushAccountLeaves(sideRows, rowId);
        });
      });
    };

    // Statistics block (summary statement): Rooms Available / Rooms Sold /
    // Occupancy % / ADR / RevPAR from the room-stat mappings, with rooms
    // revenue = the operated unit(s) named Rooms. Counts and rates, never
    // folded into the monetary totals; % Rev stays blank on these rows.
    const pushStatsBlock = () => {
      const roomsUnits = bySection[USALI_SECTIONS.OPERATED].filter((u) => ROOMS_UNIT_NAME.test(u.label || ''));
      const roomsRev = new Map(); // suffix → { a, b }
      roomsUnits.forEach((u) =>
        u.rows.forEach((r) => {
          if (!INCOME_TYPES.has(normType(r.accountType))) return;
          [String(r.month).slice(0, 10), 'total'].forEach((m) => {
            const cur = roomsRev.get(m) || { a: 0, b: 0 };
            cur.a += Number(r.actual) || 0;
            cur.b += Number(r.budget) || 0;
            roomsRev.set(m, cur);
          });
        })
      );
      const measures = [
        { id: 'AVAIL', label: 'Rooms Available', format: 'number', pick: (rm) => ({ a: rm.avail, b: rm.budgetAvail }) },
        { id: 'SOLD', label: 'Rooms Sold', format: 'number', pick: (rm) => ({ a: rm.occ, b: rm.budgetOcc }) },
        { id: 'OCC', label: 'Occupancy %', format: 'percent1', pick: (rm) => ({ a: pct(rm.occ, rm.avail), b: pct(rm.budgetOcc, rm.budgetAvail) }) },
        { id: 'ADR', label: 'ADR', format: 'number2', pick: (rm, rev) => ({ a: ratio(rev?.a, rm.occ), b: ratio(rev?.b, rm.budgetOcc) }) },
        { id: 'REVPAR', label: 'RevPAR', format: 'number2', pick: (rm, rev) => ({ a: ratio(rev?.a, rm.avail), b: ratio(rev?.b, rm.budgetAvail) }) },
      ];
      const statRows = [];
      let any = false;
      measures.forEach((ms) => {
        const cells = {};
        allSuffixes.forEach((m) => {
          const rm = metricIdx.locRooms(loc.id, m);
          if (!rm || (!rm.avail && !rm.occ && !rm.budgetAvail && !rm.budgetOcc)) return;
          const { a, b } = ms.pick(rm, roomsRev.get(m));
          const av = a === null || a === undefined || !Number.isFinite(a) ? null : a;
          const bv = b === null || b === undefined || !Number.isFinite(b) ? null : b;
          if (av === null && bv === null) return;
          any = true;
          setCells(cells, m, av, bv, av !== null && bv !== null ? av - bv : null, null);
        });
        statRows.push({ id: `${locRowId}:STATS:${ms.id}`, parentId: `${locRowId}:STATS`, label: ms.label, cells, format: ms.format });
      });
      if (!any) return;
      const occ = statRows.find((r) => r.id.endsWith(':OCC'));
      rows.push({ id: `${locRowId}:STATS`, parentId: locRowId, label: 'Statistics · Occ %', cells: { ...(occ?.cells || {}) }, format: 'percent1', variant: 'subtotal' });
      rows.push(...statRows);
    };

    if (stats) pushStatsBlock();
    if (layout === 'statement') {
      pushStatementBands();
    } else {
      bySection[USALI_SECTIONS.OPERATED].forEach((u) => pushUnit(u, false));
    }
    if (bySection[USALI_SECTIONS.OPERATED].length > 0) {
      pushLine('TODP', bands ? 'Departmental Profit' : 'Total Operated Department Profit', bandBuckets[USALI_SECTIONS.OPERATED]);
    }
    pushCostBand(USALI_SECTIONS.UNDISTRIBUTED, 'UND', 'Undistributed Expenses');
    const gopBucket = new Map();
    mergeBucket(gopBucket, bandBuckets[USALI_SECTIONS.OPERATED]);
    mergeBucket(gopBucket, bandBuckets[USALI_SECTIONS.UNDISTRIBUTED]);
    pushLine('GOP', 'Gross Operating Profit (GOP)', gopBucket);

    // Below the GOP line, in statement order. Each intermediate profit line
    // appears only when the band above it has activity, so an org that
    // classifies nothing below GOP still reads GOP → (fixed charges) → Net
    // Income; one that tags management fees and interest/depreciation gets
    // Income Before Fixed Charges and EBITDA in between.
    const hasBelowGop = BELOW_GOP_SECTIONS.some((k) => bySection[k].length > 0);
    if (hasBelowGop) {
      const running = new Map();
      mergeBucket(running, gopBucket);
      if (pushCostBand(USALI_SECTIONS.MANAGEMENT_FEE, 'MGMT', 'Management Fees')) {
        mergeBucket(running, bandBuckets[USALI_SECTIONS.MANAGEMENT_FEE]);
        pushLine('IBFC', 'Income Before Fixed Charges', running);
      }
      if (pushCostBand(USALI_SECTIONS.NON_OPERATING, 'FIXED', 'Fixed Charges')) {
        mergeBucket(running, bandBuckets[USALI_SECTIONS.NON_OPERATING]);
        if (bySection[USALI_SECTIONS.BELOW_EBITDA].length > 0) pushLine('EBITDA', 'EBITDA', running);
      }
      if (pushCostBand(USALI_SECTIONS.BELOW_EBITDA, 'BELOW', 'Interest, Depreciation & Other')) {
        mergeBucket(running, bandBuckets[USALI_SECTIONS.BELOW_EBITDA]);
      }
      pushLine('NET', 'Net Income', running);
    }
  });

  const footerCells = {};
  (data.totals?.byMonth || []).forEach((m) => setCells(footerCells, m.month, m.actual, m.budget, m.variance, m.forecast));
  const portfolio = data.totals?.portfolio;
  if (portfolio) setCells(footerCells, 'total', portfolio.actual, portfolio.budget, portfolio.variance, portfolio.forecast);
  allSuffixes.forEach((m) => {
    if (footerCells[`a|${m}`] === undefined) return;
    setMetricCells(footerCells, m, footerCells[`a|${m}`], metricIdx.portfolioBase(m), metricIdx.portfolioRooms(m), footerCells[`b|${m}`], metricIdx.portfolioBase(m, 'b'));
  });

  return { columns, rows, footer: { label: 'Portfolio Total', cells: footerCells } };
}

// ---------------------------------------------------------------------------
// Summary Operating Statement — the one-page owner's statement: the selected
// period's columns on the left, the description in the middle, year-to-date
// on the right, rows = the USALI statement with cost bands collapsed to their
// headings and a Statistics block on top. Each column set reads one dataset
// (period / YTD / their prior-year twins) through the statement-layout USALI
// builder and the two are merged by row id, so every row keeps its GL drill
// and cross-filter from the underlying statement.
// ---------------------------------------------------------------------------

export const SUMMARY_SIDES = [
  { suffix: 'P', side: 'left' },
  { suffix: 'Y', side: 'right' },
];

/**
 * Columns for the summary statement. Static per (labels, showBudget) — no
 * month dependency — so the page can hold column prefs before data arrives.
 * `actualCellRender(suffix)` receives 'P' | 'Y'.
 */
export function buildFinancialSummaryColumns({ showBudget = true, periodLabel = 'Period', ytdLabel = 'Year to Date', actualCellRender } = {}) {
  const columns = [];
  SUMMARY_SIDES.forEach(({ suffix, side }) => {
    const group = suffix === 'P' ? `Period · ${periodLabel}` : `Year to Date · ${ytdLabel}`;
    const common = { group, side };
    columns.push({
      key: `a|${suffix}`, label: 'Actual', facet: 'actual', format: 'number', ...common,
      description: 'Posted general-ledger activity for the column’s period, shown positive for both revenue and expenses.',
      ...(actualCellRender ? { render: actualCellRender(suffix) } : {}),
    });
    columns.push({
      key: `pr|${suffix}`, label: '%', facet: 'pctRev', format: 'percent1', ...common,
      description: 'Actual as a percent of revenue: a department’s own revenue for department expense lines, total revenue for everything else.',
    });
    if (showBudget) {
      columns.push({ key: `b|${suffix}`, label: 'Budget', facet: 'budget', variant: 'budget', format: 'number', ...common, description: 'Budget for the column’s period.' });
      columns.push({ key: `pb|${suffix}`, label: 'Bud %', facet: 'pctBudget', variant: 'budget', format: 'percent1', ...common, description: 'Budget as a percent of budget revenue, on the same basis as the actual %.' });
      columns.push({ key: `v|${suffix}`, label: 'Var', facet: 'variance', variant: 'variance', format: 'number', ...common, description: 'Actual vs Budget, favorable-oriented: revenue over budget and expenses under budget both show positive.' });
    }
    columns.push({ key: `ly|${suffix}`, label: 'LY', facet: 'ly', variant: 'budget', format: 'number', ...common, description: 'Actual for the same period one year earlier.' });
    columns.push({ key: `ply|${suffix}`, label: 'LY %', facet: 'pctLy', variant: 'budget', format: 'percent1', ...common, description: 'Last year’s actual as a percent of last year’s revenue.' });
  });
  return columns;
}

/**
 * @param datasets { period, ytd, lyPeriod, lyYtd } — financialPnl payloads
 *   (account grain); any may be null while loading, its columns stay blank.
 */
export function buildFinancialSummaryMatrix(datasets, { showBudget = true, actualCellRender, periodLabel, ytdLabel } = {}) {
  const columns = buildFinancialSummaryColumns({ showBudget, periodLabel, ytdLabel, actualCellRender });
  const { period, ytd, lyPeriod, lyYtd } = datasets || {};
  const build = (d) => (d ? buildFinancialUsaliMatrix(d, { showBudget: true, layout: 'statement', bands: true, stats: true }) : null);
  const mx = { P: build(period), Y: build(ytd), LP: build(lyPeriod), LY: build(lyYtd) };
  if (!mx.P && !mx.Y) return { columns, rows: [], footer: null };

  // Row skeleton: YTD (the superset, normally) leads; rows only the other
  // datasets know about slot in beside their siblings.
  const order = [];
  const byId = new Map();
  const adopt = (m) => {
    if (!m) return;
    const seeding = order.length === 0;
    m.rows.forEach((r) => {
      if (byId.has(r.id)) return;
      const { cells, ...rest } = r;
      byId.set(r.id, { ...rest, cells: {} });
      if (seeding) {
        order.push(r.id);
        return;
      }
      const lastSibling = order.reduce((acc, id, i) => (byId.get(id).parentId === r.parentId ? i : acc), -1);
      if (lastSibling >= 0) {
        // after the sibling's whole subtree
        let j = lastSibling + 1;
        const inSubtree = (id) => {
          let cur = byId.get(id);
          while (cur && cur.parentId != null) {
            if (cur.parentId === order[lastSibling]) return true;
            cur = byId.get(cur.parentId);
          }
          return false;
        };
        while (j < order.length && inSubtree(order[j])) j += 1;
        order.splice(j, 0, r.id);
      } else if (r.parentId != null && order.includes(r.parentId)) {
        order.splice(order.indexOf(r.parentId) + 1, 0, r.id);
      } else {
        order.push(r.id);
      }
    });
  };
  adopt(mx.Y);
  adopt(mx.P);
  adopt(mx.LY);
  adopt(mx.LP);

  const copy = (into, from, suffix) => {
    if (!from) return;
    ['a', 'pr', 'b', 'pb', 'v'].forEach((k) => {
      const v = from[`${k}|total`];
      if (v !== undefined) into[`${k}|${suffix}`] = v;
    });
  };
  const copyLy = (into, from, suffix) => {
    if (!from) return;
    if (from['a|total'] !== undefined) into[`ly|${suffix}`] = from['a|total'];
    if (from['pr|total'] !== undefined) into[`ply|${suffix}`] = from['pr|total'];
  };
  const index = (m) => (m ? new Map(m.rows.map((r) => [r.id, r.cells])) : new Map());
  const idx = { P: index(mx.P), Y: index(mx.Y), LP: index(mx.LP), LY: index(mx.LY) };
  const rows = order.map((id) => {
    const row = byId.get(id);
    copy(row.cells, idx.P.get(id), 'P');
    copy(row.cells, idx.Y.get(id), 'Y');
    copyLy(row.cells, idx.LP.get(id), 'P');
    copyLy(row.cells, idx.LY.get(id), 'Y');
    return row;
  });

  const footerCells = {};
  copy(footerCells, mx.P?.footer?.cells, 'P');
  copy(footerCells, mx.Y?.footer?.cells, 'Y');
  copyLy(footerCells, mx.LP?.footer?.cells, 'P');
  copyLy(footerCells, mx.LY?.footer?.cells, 'Y');
  return { columns, rows, footer: { label: 'Portfolio Total', cells: footerCells } };
}

// ---------------------------------------------------------------------------
// Statement layout (Excel export) — the same P&L matrix re-read as a printed
// income statement: detail first, totals after, sections named. The screen
// matrix is a collapsible tree (parent carries the total, children hang under
// it); an accountant reads the opposite order, so this rebuilds it as
//
//   STATISTICS            (the stat band, when mapped)
//   REVENUE
//     <department>
//       <account>…
//       Total <department>
//   Total Revenue
//   EXPENSES              (every non-income account type — the same
//     …                    income-vs-expense split the USALI views use)
//   Total Expenses
//   NET INCOME            (the location subtotal — revenue less expenses)
//
// Rows come back flat, each tagged with the indent and the style the writer
// needs; no cell values are recomputed, so every line ties to the screen.
// ---------------------------------------------------------------------------

// Amount cells (actual / budget / variance / forecast) sum across rows; the
// metric facets (% Rev, $POR, $PAR) are ratios and never do, so a synthesized
// total leaves them blank rather than adding percentages together.
const AMOUNT_CELL_PREFIXES = ['a|', 'b|', 'v|', 'f|'];

function sumAmountCells(rows) {
  const out = {};
  rows.forEach((row) => {
    Object.entries(row.cells || {}).forEach(([key, value]) => {
      if (!AMOUNT_CELL_PREFIXES.some((p) => key.startsWith(p))) return;
      if (value === null || value === undefined) return;
      out[key] = (out[key] ?? 0) + Number(value);
    });
  });
  return out;
}

/**
 * Re-read a P&L matrix (buildFinancialPnlReportMatrix) as statement rows:
 * `{ label, cells, indent, style, format? }`, where style is one of
 * title · section · group · detail · groupTotal · sectionTotal · net · blank.
 * Returns [] when the matrix carries no location rows.
 */
export function buildFinancialPnlStatementRows(matrix) {
  const source = (matrix?.rows || []).filter((r) => !r.fullRender);
  if (source.length === 0) return [];

  const childrenOf = new Map(); // parentId (null = root) → rows, in matrix order
  source.forEach((row) => {
    const key = row.parentId ?? null;
    if (!childrenOf.has(key)) childrenOf.set(key, []);
    childrenOf.get(key).push(row);
  });
  const kids = (row) => childrenOf.get(row.id) || [];

  const out = [];
  const push = (style, label, indent, cells, format) =>
    out.push({ style, label, indent, cells: cells || null, ...(format ? { format } : {}) });
  const blank = () => {
    if (out.length > 0 && out[out.length - 1].style !== 'blank') push('blank', '', 0, null);
  };

  // Stat leaves keep the band's own nesting (metric → GL accounts) — counts,
  // not currency, so they stay outside the revenue/expense arithmetic.
  const pushStats = (band) => {
    push('section', 'STATISTICS', 0, null);
    kids(band).forEach((metric) => {
      push('group', metric.label, 1, metric.cells, metric.format);
      kids(metric).forEach((leaf) => push('detail', leaf.label, 2, leaf.cells, leaf.format));
    });
  };

  // One section (Revenue or Expenses) for one location. Departments hang
  // straight off the section heading: the account TYPE tier (Revenue vs Other
  // Income, Expense vs Other Expense) is a Lodgeic classification, not a line
  // an accountant reads — it only orders the departments beneath the heading.
  // Returns the section's total cells, or null when this side is out of scope.
  const pushSection = (heading, totalLabel, typeRows) => {
    if (typeRows.length === 0) return null;
    push('section', heading, 0, null);
    typeRows.forEach((typeRow) => {
      kids(typeRow).forEach((dept) => {
        const accounts = kids(dept);
        if (accounts.length === 0) {
          // Department-grain payload — account leaves have not arrived, so the
          // department line carries its own amounts.
          push('detail', dept.label, 1, dept.cells, dept.format);
          return;
        }
        push('group', dept.label, 1, null);
        accounts.forEach((acct) => push('detail', acct.label, 2, acct.cells, acct.format));
        push('groupTotal', `Total ${dept.label}`, 1, dept.cells);
      });
    });
    // One account type IS the section, ratios and all; more than one has to be
    // added up, and the ratio facets sit that out (see sumAmountCells).
    const cells = typeRows.length > 1 ? sumAmountCells(typeRows) : typeRows[0].cells;
    push('sectionTotal', totalLabel, 0, cells);
    return cells;
  };

  const locations = (childrenOf.get(null) || []).filter((r) => r.stmt?.kind === 'location');
  const revenueTotals = [];
  const expenseTotals = [];

  locations.forEach((loc) => {
    blank();
    push('title', loc.label, 0, null);
    const children = kids(loc);
    const band = children.find((r) => r.stmt?.kind === 'statsBand');
    if (band) {
      pushStats(band);
      blank();
    }
    const types = children.filter((r) => r.stmt?.kind === 'type');
    const revenue = pushSection('REVENUE', 'Total Revenue', types.filter((r) => r.stmt.income));
    blank();
    const expenses = pushSection('EXPENSES', 'Total Expenses', types.filter((r) => !r.stmt.income));
    if (revenue) revenueTotals.push({ cells: revenue });
    if (expenses) expenseTotals.push({ cells: expenses });
    // Net income only reads as net income with both sides in scope — filtered
    // to one account type the section total already said everything.
    if (revenue && expenses) {
      blank();
      push('net', 'NET INCOME', 0, loc.cells);
    }
  });

  // Consolidated block — only earns its rows when more than one property is
  // in scope (with one, it would restate that property's own totals).
  if (locations.length > 1 && matrix.footer) {
    blank();
    push('title', 'PORTFOLIO — ALL PROPERTIES', 0, null);
    if (revenueTotals.length > 0) push('sectionTotal', 'Total Revenue', 0, sumAmountCells(revenueTotals));
    if (expenseTotals.length > 0) push('sectionTotal', 'Total Expenses', 0, sumAmountCells(expenseTotals));
    push('net', 'NET INCOME', 0, matrix.footer.cells);
  }

  while (out.length > 0 && out[0].style === 'blank') out.shift();
  return out;
}

/**
 * Statement rows for the USALI views (Dept / GOP), which already read in
 * statement order — departmental profit → GOP → net income. Nothing is
 * rebuilt: the tree's nesting becomes the indent and the row variants the
 * writer's styles, so the export mirrors the screen with statement typography.
 */
export function buildUsaliStatementRows(matrix) {
  const source = (matrix?.rows || []).filter((r) => !r.fullRender);
  if (source.length === 0) return [];
  const byId = new Map(source.map((r) => [r.id, r]));
  const indentOf = (row) => {
    let indent = 0;
    let cur = row;
    while (cur?.parentId != null && byId.has(cur.parentId)) {
      indent += 1;
      cur = byId.get(cur.parentId);
    }
    return indent;
  };

  const out = [];
  let locationCount = 0;
  source.forEach((row) => {
    const indent = indentOf(row);
    if (indent === 0) {
      locationCount += 1;
      if (out.length > 0) out.push({ style: 'blank', label: '', indent: 0, cells: null });
      out.push({ style: 'title', label: row.label, indent: 0, cells: null });
      // The location row carries net income; the USALI tree restates it below
      // as its own Net Income line, so the title stays a bare heading.
      return;
    }
    // Net Income is the one double-ruled line; the other USALI summary lines
    // (departmental profit, undistributed, GOP) rule single, and the unit /
    // side rows above them read as groups.
    const isNet = /^net income$/i.test(row.label || '');
    const summary = row.variant === 'emphasis' || (row.variant === 'subtotal' && indent === 1);
    const style = isNet ? 'net' : summary ? 'sectionTotal' : indent <= 2 ? 'group' : 'detail';
    out.push({ style, label: row.label, indent, cells: row.cells || null, ...(row.format ? { format: row.format } : {}) });
  });
  if (locationCount > 1 && matrix.footer) {
    out.push({ style: 'blank', label: '', indent: 0, cells: null });
    out.push({ style: 'title', label: 'PORTFOLIO — ALL PROPERTIES', indent: 0, cells: null });
    out.push({ style: 'net', label: 'NET INCOME', indent: 0, cells: matrix.footer.cells });
  }
  return out;
}

/**
 * Raw data export — the income statement flattened to the Aperture budget
 * template layout: one row per statement line per location, with identifier
 * columns (Lodgeic COA id, GL account number, department) filled on account
 * rows and one "<Mon YYYY> Actual" column per month plus "Total Actual".
 * Each location reads Net Income first, then the type → department → account
 * tree, with the Statistics band trailing — the template's row order. Values
 * are actuals only, blank where no activity posted. Returns an array of
 * arrays (header first) ready for a worksheet, or null without data.
 */
export function buildFinancialPnlRawExport(data) {
  if (!data) return null;
  const matrix = buildFinancialPnlReportMatrix(data);
  if (!matrix.rows.length) return null;
  const { months } = collectMonthEntries(data);
  const suffixes = [...months, 'total'];
  const header = [
    'Location',
    'Account',
    'Lodgeic Account ID',
    'Account Number',
    'Department',
    ...months.map((m) => `${format(parseIsoMonth(m), 'MMM yyyy')} Actual`),
    'Total Actual',
  ];

  const rows = [];
  let location = '';
  // The on-screen statement leads with Statistics; the template trails with
  // them — hold each location's stat rows and flush them at the block's end.
  let statBlock = [];
  const flushStats = () => {
    rows.push(...statBlock);
    statBlock = [];
  };
  matrix.rows.forEach((row) => {
    const isRoot = row.parentId === undefined || row.parentId === null;
    if (isRoot) {
      flushStats();
      location = row.label;
    }
    const d = row.drill;
    const rec = [
      location,
      // The location row carries the location's net income — name it so.
      isRoot ? 'Net Income' : row.label,
      d?.accountId ?? '',
      d?.accountNumber || '',
      d?.department && d.department !== '(No department)' ? titleCase(d.department) : '',
      ...suffixes.map((s) => {
        const v = row.cells?.[`a|${s}`];
        return v === null || v === undefined ? '' : Number(v);
      }),
    ];
    if (!isRoot && /^L\d+:STATS(?::|$)/.test(String(row.id))) statBlock.push(rec);
    else rows.push(rec);
  });
  flushStats();
  return [header, ...rows];
}

export function buildFinancialPnlArtifactMatrix(data, { showBudget = true, actualCellRender, defaultLocationId = null } = {}) {
  if (!data || typeof data !== 'object') return { columns: [], rows: [], footer: null };

  const monthly = data.viewMode === 'monthly' && Array.isArray(data.months) && data.months.length > 0;
  const rawMonthEntries = monthly
    ? data.months.map((m) => ({ key: monthKey(m), label: m.label || monthLabelFromKey(monthKey(m), false) }))
    : [];
  const spanYears = rawMonthEntries.length > 1 && rawMonthEntries[0].key.slice(0, 4) !== rawMonthEntries[rawMonthEntries.length - 1].key.slice(0, 4);
  const monthEntries = rawMonthEntries.map((m) => ({
    ...m,
    label: spanYears && !/\d{4}/.test(String(m.label || '')) ? monthLabelFromKey(m.key, true) : m.label,
  }));
  const columns = buildColumns({ monthEntries, showBudget, actualCellRender });

  const rows = [];
  const pushNode = (node, parentId = null, path = [], categoryType = null) => {
    const label = node.glCode ? `${node.glCode} · ${node.name}` : node.name;
    const id = [...path, node.glCode || node.chartOfAccountId || node.name].join(':') || label;
    const cells = {};
    if (monthly) {
      monthEntries.forEach((m, idx) => {
        const actual = node.monthlyActual?.[idx] ?? null;
        const budget = node.monthlyBudget?.[idx] ?? null;
        setCells(cells, m.key, actual, budget, actual != null && budget != null ? actual - budget : null);
      });
      const totalActual = node.totalActual ?? null;
      const totalBudget = node.totalBudget ?? null;
      setCells(cells, 'total', totalActual, totalBudget, totalActual != null && totalBudget != null ? totalActual - totalBudget : null);
    } else {
      const actual = node.actual ?? node.totalActual ?? null;
      const budget = node.budget ?? node.totalBudget ?? null;
      setCells(cells, 'total', actual, budget, actual != null && budget != null ? actual - budget : null);
    }
    const variant = parentId && (node.children?.length || categoryType === node.type) ? 'subtotal' : undefined;
    const drill = node.chartOfAccountId && defaultLocationId
      ? {
          accountId: node.chartOfAccountId,
          accountLabel: label,
          locationId: defaultLocationId,
        }
      : undefined;
    rows.push({ id, parentId, label, cells, variant, ...(drill ? { drill } : {}) });
    (node.children || []).forEach((child) => pushNode(child, id, [...path, node.glCode || node.name], node.type || categoryType));
  };

  (data.categories || []).forEach((category) => pushNode(category));

  const footerCells = {};
  if (data.netIncome) {
    if (monthly) {
      monthEntries.forEach((m, idx) => {
        const actual = data.netIncome.monthlyActual?.[idx] ?? null;
        const budget = data.netIncome.monthlyBudget?.[idx] ?? null;
        setCells(footerCells, m.key, actual, budget, actual != null && budget != null ? actual - budget : null);
      });
      const totalActual = data.netIncome.totalActual ?? data.netIncome.actual ?? null;
      const totalBudget = data.netIncome.totalBudget ?? data.netIncome.budget ?? null;
      setCells(footerCells, 'total', totalActual, totalBudget, totalActual != null && totalBudget != null ? totalActual - totalBudget : null);
    } else {
      const actual = data.netIncome.actual ?? data.netIncome.totalActual ?? null;
      const budget = data.netIncome.budget ?? data.netIncome.totalBudget ?? null;
      setCells(footerCells, 'total', actual, budget, actual != null && budget != null ? actual - budget : null);
    }
  }

  return {
    columns,
    rows,
    footer: data.netIncome ? { label: 'Net Income', cells: footerCells } : null,
  };
}
